const req = (method, path, body) => ({ method, path, body });
const remember = (response, ctx) => {
  ctx.instanceId = response?.id;
  ctx.instanceIds = [...new Set([...(ctx.instanceIds || []), response?.id].filter(Boolean))];
};
const quote = (capabilities, extraRequirements) => ({
  title: '按能力和预算报价',
  note: '客户端只描述资源、能力与预算，不接触 Provider 凭证或底层资源 ID。',
  build: (ctx, params) => req('POST', '/v1/quotes', {
    requirements: { capabilities, ...extraRequirements },
    ...(params.maxHourly ? { maxEstimatedHourlyUsd: String(params.maxHourly) } : {}),
  }),
  after: (response, ctx) => { ctx.quoteId = response?.quoteId; ctx.offering = response?.offering; },
  summary: (response) => [
    { k: '选中', v: response?.offering?.name },
    { k: '每小时', v: response?.estimatedHourlyUsd ? `$${response.estimatedHourlyUsd}` : '—' },
    { k: '首次预留', v: response?.initialReservationUsd ? `$${response.initialReservationUsd}` : '—' },
  ],
});
const create = (scenario, metadata = {}) => ({
  title: '创建隔离环境',
  build: (ctx) => req('POST', '/v1/sandboxes', {
    selection: { quoteId: ctx.quoteId },
    policy: { resumeOnAccess: false },
    idempotencyKey: `playground:${scenario}:${crypto.randomUUID()}`,
    metadata: { client: 'sandbox-playground', scenario, ...metadata },
  }),
  after: remember,
  summary: (response) => [{ k: '实例', v: response?.id }, { k: '状态', v: response?.observedState }],
});
const terminate = (pickId = (ctx) => ctx.instanceId) => ({
  title: '销毁并停止计费',
  note: 'Playground 在正常结束和异常中断时都会尝试清理本次实例。',
  build: (ctx) => req('POST', `/v1/sandboxes/${pickId(ctx)}/terminate`, {
    idempotencyKey: `playground:terminate:${crypto.randomUUID()}`,
  }),
});

const AGENT_SCRIPT = `mkdir -p agent-demo
cat > agent-demo/math.js <<'JS'
export const multiply = (a, b) => a * b;
JS
cat > agent-demo/test.mjs <<'JS'
import { multiply } from './math.js';
if (multiply(6, 7) !== 42) throw new Error('test failed');
console.log('AI_AGENT_TEST_OK=42');
JS
node agent-demo/test.mjs`;

export const openAiSandboxSdkExample = (model = 'deepseek-v4-pro') => `import { OpenAIProvider, Runner } from '@openai/agents';
import { Manifest, SandboxAgent, shell } from '@openai/agents/sandbox';
import { XapiAgentsSandboxClient } from 'xapi-to/openai-sandbox';

const sandboxApiKey = process.env.XAPI_SANDBOX_KEY;
const aiApiKey = process.env.XAPI_AI_KEY;
if (!sandboxApiKey) throw new Error('XAPI_SANDBOX_KEY is required');
if (!aiApiKey) throw new Error('XAPI_AI_KEY is required');

const sandbox = new XapiAgentsSandboxClient({
  apiKey: sandboxApiKey,
  sandboxHost: 'sandbox.xapi.to',
  provider: 'daytona',
  model: '${model}',
});

// ai.xapi.to exposes an OpenAI Chat Completions-compatible endpoint.
const modelProvider = new OpenAIProvider({
  apiKey: aiApiKey,
  baseURL: 'https://ai.xapi.to/v1',
  useResponses: false,
  strictFeatureValidation: true,
});
const runner = new Runner({ modelProvider, tracingDisabled: true });
const agent = new SandboxAgent({
  name: 'xAPI DeepSeek sandbox agent',
  model: '${model}',
  defaultManifest: new Manifest({ root: sandbox.workspaceRoot }),
  capabilities: [shell()],
  instructions: 'Use shell to do the task inside the sandbox and verify the result.',
});

try {
  const result = await runner.run(agent, 'Write SDK_OK=42 to result.txt, then read it.', {
    maxTurns: 8,
    sandbox: { client: sandbox },
  });
  console.log(result.finalOutput);
} finally {
  await sandbox.lastSession?.close();
}`;

export const EXTRA_SANDBOX_SCENARIOS = [
  {
    id: 'openai-sandbox-sdk',
    kind: 'ai',
    title: 'OpenAI Sandbox SDK + DeepSeek',
    gateway: 'daytona',
    blurb: '使用官方 OpenAI Agents SDK 的 SandboxAgent：模型经 ai.xapi.to 调用 DeepSeek，Shell 工具经 xAPI 创建 Daytona 隔离环境。页面真实验证模型、报价、实例、命令、文件与审计链路；完整 Agent loop 请运行 SDK 示例。',
    sdkExample: (params) => openAiSandboxSdkExample(params.model),
    params: [
      { key: 'model', label: '模型', type: 'text', default: 'deepseek-v4-pro', hint: '通过 ai.xapi.to 的 OpenAI Chat Completions 兼容接口调用。' },
      { key: 'maxHourly', label: '每小时价格上限（USD）', type: 'text', default: '0.20' },
    ],
    steps: [
      {
        title: '验证 DeepSeek 模型网关',
        note: '验证 ai.xapi.to 的 OpenAI Chat Completions 兼容传输；完整 SandboxAgent 工具循环由右侧 SDK 示例和 CLI 测试覆盖。',
        absolute: () => 'https://ai.xapi.to/v1/chat/completions',
        build: (ctx, params) => req('POST', '/v1/chat/completions', {
          model: params.model,
          messages: [{ role: 'user', content: 'Reply exactly XAPI_DEEPSEEK_OK' }],
          temperature: 0,
          max_tokens: 128,
        }),
        summary: (response) => [
          { k: '模型', v: response?.model },
          { k: '响应', v: response?.choices?.[0]?.message?.content },
        ],
      },
      quote(['exec', 'files']),
      create('openai-sandbox-sdk', { sdk: '@openai/agents', modelGateway: 'ai.xapi.to' }),
      { wait: 'RUNNING', title: '等待 SDK Sandbox 就绪' },
      {
        title: 'Shell 写入验证产物',
        build: (ctx) => req('POST', `/v1/sandboxes/${ctx.instanceId}/commands`, {
          command: "mkdir -p /home/daytona/openai-xapi && printf 'OPENAI_XAPI_SANDBOX_OK=42\\n' > /home/daytona/openai-xapi/result.txt",
          timeoutSeconds: 120,
        }),
        summary: (response) => [{ k: 'exitCode', v: String(response?.exitCode ?? '') }],
      },
      {
        title: 'Shell 读取并核验产物',
        build: (ctx) => req('POST', `/v1/sandboxes/${ctx.instanceId}/commands`, {
          command: 'cat result.txt',
          workingDirectory: '/home/daytona/openai-xapi',
          timeoutSeconds: 120,
        }),
        summary: (response) => ({
          rows: [{ k: 'exitCode', v: String(response?.exitCode ?? '') }],
          block: response?.stdout || response?.stderr,
        }),
      },
      terminate(),
      { wait: 'TERMINATED', title: '确认 SDK Sandbox 已销毁' },
    ],
  },
  {
    id: 'ai-coding-agent',
    kind: 'ai',
    title: '代码执行与测试工作区（Agent 示例）',
    blurb: '页面真实验证 quote/create/exec/files/terminate 这些 Sandbox 原语。接入模型后，任务规划和命令选择仍由上层 Agent 负责，并不是 xAPI Gateway 的工作流功能。',
    params: [
      { key: 'task', label: '交给 Agent 的任务', type: 'textarea', default: '实现 multiply 函数并运行测试。', hint: '任务会写入工作区，模拟 Agent 的上下文文件。' },
      { key: 'command', label: 'Agent 执行计划', type: 'textarea', default: AGENT_SCRIPT, hint: '真实送入 shell；可以替换成 git clone、npm test 或 pytest。' },
      { key: 'maxHourly', label: '每小时价格上限（USD）', type: 'text', default: '0.20' },
    ],
    steps: [
      quote(['exec', 'files']),
      create('ai-coding-agent'),
      { wait: 'RUNNING', title: '等待 Agent 工作区就绪' },
      {
        title: '写入任务上下文',
        build: (ctx, params) => req('POST', `/v1/sandboxes/${ctx.instanceId}/files`, {
          path: 'TASK.md', encoding: 'utf8', content: params.task,
        }),
      },
      {
        title: 'Agent 修改代码并运行测试',
        build: (ctx, params) => req('POST', `/v1/sandboxes/${ctx.instanceId}/commands`, {
          command: params.command, timeoutSeconds: 180,
        }),
        summary: (response) => ({
          rows: [{ k: 'exitCode', v: String(response?.exitCode ?? '') }],
          block: response?.stdout || response?.stderr,
        }),
      },
      {
        title: '读取 Agent 产物',
        build: (ctx) => req('GET', `/v1/sandboxes/${ctx.instanceId}/files?path=${encodeURIComponent('agent-demo/math.js')}`),
        summary: (response) => ({ block: response?.content }),
      },
      terminate(),
      { wait: 'TERMINATED', title: '确认销毁完成' },
    ],
  },
  {
    id: 'ci-repair',
    kind: 'ai',
    title: '隔离 CI Runner',
    blurb: '页面用确定性命令验证隔离环境、依赖安装和测试重跑。PR 修复、依赖升级与重试策略由 CI 或 Agent 应用编排，xAPI 只提供 Runner 所需的 Sandbox 能力。',
    params: [
      { key: 'command', label: 'CI 命令', type: 'textarea', default: "python3 - <<'PY'\nfrom pathlib import Path\np=Path('ci-demo.py')\np.write_text('def add(a,b): return a+b\\n')\nassert __import__('runpy').run_path(str(p))['add'](20,22) == 42\nprint('CI_REPAIR_OK')\nPY", hint: '替换为 git clone ... && npm ci && npm test。' },
      { key: 'maxHourly', label: '每小时价格上限（USD）', type: 'text', default: '0.20' },
    ],
    steps: [
      quote(['exec', 'files']), create('ci-repair'),
      { wait: 'RUNNING', title: '等待 CI Runner' },
      {
        title: '执行复现与验证命令',
        build: (ctx, params) => req('POST', `/v1/sandboxes/${ctx.instanceId}/commands`, { command: params.command, timeoutSeconds: 600 }),
        summary: (response) => ({ rows: [{ k: 'exitCode', v: String(response?.exitCode ?? '') }], block: response?.stdout || response?.stderr }),
      },
      terminate(), { wait: 'TERMINATED', title: '确认销毁完成' },
    ],
  },
  {
    id: 'data-analysis',
    kind: 'ai',
    title: '数据分析与报告',
    blurb: '页面真实验证上传数据、隔离计算和取回结构化报告；报告可继续交给模型总结，模型调用不在本场景内伪装执行。',
    params: [
      { key: 'csv', label: '输入 CSV', type: 'textarea', default: 'day,revenue\nMon,12\nTue,18\nWed,12\n', hint: '数据只写入本次 Sandbox。' },
      { key: 'maxHourly', label: '每小时价格上限（USD）', type: 'text', default: '0.20' },
    ],
    steps: [
      quote(['exec', 'files']), create('data-analysis'),
      { wait: 'RUNNING', title: '等待分析环境' },
      { title: '上传数据', build: (ctx, params) => req('POST', `/v1/sandboxes/${ctx.instanceId}/files`, { path: 'input.csv', encoding: 'utf8', content: params.csv }) },
      {
        title: '执行分析并生成报告',
        build: (ctx) => req('POST', `/v1/sandboxes/${ctx.instanceId}/commands`, {
          command: "python3 - <<'PY'\nimport csv,json\nwith open('input.csv') as f: rows=list(csv.DictReader(f))\nvalues=[float(r['revenue']) for r in rows]\nreport={'rows':len(rows),'total':sum(values),'average':sum(values)/len(values)}\nopen('report.json','w').write(json.dumps(report,indent=2))\nprint(json.dumps(report))\nPY",
          timeoutSeconds: 60,
        }),
        summary: (response) => ({ block: response?.stdout || response?.stderr }),
      },
      { title: '取回结构化报告', build: (ctx) => req('GET', `/v1/sandboxes/${ctx.instanceId}/files?path=report.json`), summary: (response) => ({ block: response?.content }) },
      terminate(), { wait: 'TERMINATED', title: '确认销毁完成' },
    ],
  },
  {
    id: 'multi-agent',
    kind: 'ai',
    title: '并行隔离实例（多 Agent 示例）',
    blurb: '页面真实创建两台独立实例，验证并发工作者不会共享依赖和临时文件。角色分工、模型 loop、调度与结果汇总全部由上层编排器负责。',
    params: [{ key: 'maxHourly', label: '每台每小时价格上限（USD）', type: 'text', default: '0.20' }],
    steps: [
      quote(['exec', 'files']),
      {
        title: '创建实现工作区',
        build: (ctx) => req('POST', '/v1/sandboxes', { selection: { quoteId: ctx.quoteId }, idempotencyKey: `playground:agent-a:${crypto.randomUUID()}`, metadata: { client: 'sandbox-playground', role: 'implementer' } }),
        after: (response, ctx) => { remember(response, ctx); ctx.agentA = response?.id; },
      },
      {
        title: '为审查工作区独立报价',
        note: '报价只能核销一次；每台实例都使用自己的报价和价格上限。',
        build: (ctx, params) => req('POST', '/v1/quotes', {
          requirements: { capabilities: ['exec', 'files'] },
          maxEstimatedHourlyUsd: String(params.maxHourly),
        }),
        after: (response, ctx) => { ctx.reviewerQuoteId = response?.quoteId; },
      },
      {
        title: '创建审查工作区',
        build: (ctx) => req('POST', '/v1/sandboxes', {
          selection: { quoteId: ctx.reviewerQuoteId },
          idempotencyKey: `playground:agent-b:${crypto.randomUUID()}`,
          metadata: { client: 'sandbox-playground', role: 'reviewer' },
        }),
        after: (response, ctx) => { remember(response, ctx); ctx.agentB = response?.id; },
      },
      { wait: 'RUNNING', ids: (ctx) => [ctx.agentA, ctx.agentB], title: '等待两个隔离工作区' },
      { title: '实现工作区独立执行', build: (ctx) => req('POST', `/v1/sandboxes/${ctx.agentA}/commands`, { command: "echo 'implementation=ready' > result.txt && cat result.txt" }) },
      { title: '审查工作区独立执行', build: (ctx) => req('POST', `/v1/sandboxes/${ctx.agentB}/commands`, { command: "echo 'review=approved' > result.txt && cat result.txt" }) },
      terminate((ctx) => ctx.agentA), terminate((ctx) => ctx.agentB),
      { wait: 'TERMINATED', ids: (ctx) => [ctx.agentA, ctx.agentB], title: '确认两个环境都已销毁' },
    ],
  },
  {
    id: 'gpu-job',
    title: 'GPU 实例与连接信息',
    gateway: 'runpod',
    blurb: '定向 RunPod，按 GPU 数量和预算选算力，再通过扩展取得真实公网连接信息。此场景会真实申请 GPU，单价通常更高。',
    params: [
      { key: 'gpuCount', label: 'GPU 数量', type: 'text', default: '1' },
      { key: 'maxHourly', label: '每小时价格上限（USD）', type: 'text', default: '1.00' },
    ],
    steps: [
      {
        ...quote([], { gpu: { count: 1 } }),
        build: (ctx, params) => req('POST', '/v1/quotes', {
          requirements: { gpu: { count: Math.max(1, Number(params.gpuCount) || 1) } },
          maxEstimatedHourlyUsd: String(params.maxHourly || '1.00'),
        }),
      },
      create('gpu-job'), { wait: 'RUNNING', title: '等待 GPU 实例' },
      { title: '读取实例与规格状态', build: (ctx) => req('GET', `/v1/sandboxes/${ctx.instanceId}`), summary: (response) => [{ k: '状态', v: response?.observedState }, { k: '规格', v: response?.offering?.name }] },
      {
        title: '取得 RunPod 连接信息',
        note: 'connectionReady=false 表示 RunPod 仍在分配公网 IP/端口，可重试这个扩展请求，不需要重建实例。',
        build: (ctx) => req('POST', `/v1/sandboxes/${ctx.instanceId}/extensions/runpod.connection_info`, {
          input: {},
          idempotencyKey: `playground:runpod-connection:${crypto.randomUUID()}`,
        }),
        until: (response) => (response?.result?.result || response?.result)?.connectionReady === true,
        retryIntervalMs: 10_000,
        timeoutMs: 240_000,
        pendingText: '等待 RunPod 分配公网 IP 与端口',
        summary: (response) => {
          const connection = response?.result?.result || response?.result || {};
          return [
            { k: '连接就绪', v: connection.connectionReady ? '是' : '分配中' },
            { k: '公网 IP', v: connection.publicIp || '尚未分配' },
            { k: '端口映射', v: Object.keys(connection.portMappings || {}).length || '尚未分配' },
          ];
        },
      },
      terminate(), { wait: 'TERMINATED', title: '确认销毁完成' },
    ],
  },
];

export const AI_TOOL_CONTRACT = [
  ['sandbox.quote', '按能力、资源和预算取得报价'],
  ['sandbox.create', '创建隔离实例；使用幂等键防止重复开机'],
  ['sandbox.exec', '执行命令，读取 exitCode/stdout/stderr'],
  ['sandbox.files', '写入任务上下文、源码与数据，取回产物'],
  ['sandbox.port', '把预览或临时 API 暴露为公网地址'],
  ['sandbox.suspend/resume', '长任务空闲时停费或降费，稍后继续'],
  ['sandbox.audit', '读取操作、状态、用量段和结算账单'],
  ['sandbox.terminate', 'finally 中强制销毁，停止计费'],
];
